import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface Source {
  title: string;
  author: string;
  type: "book" | "article" | "website" | "video" | "course" | "report";
  description: string;
  url?: string;
}

const SOURCES_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days

function sourcesKey(userId: string, nodeId: string) {
  return `node_sources_${userId}_${nodeId}`;
}

function loadSources(userId: string, nodeId: string): Source[] | null {
  try {
    const raw = localStorage.getItem(sourcesKey(userId, nodeId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.fetchedAt || Date.now() - parsed.fetchedAt > SOURCES_TTL) return null;
    return parsed.sources;
  } catch {
    return null;
  }
}

function saveSources(userId: string, nodeId: string, sources: Source[]) {
  try {
    localStorage.setItem(sourcesKey(userId, nodeId), JSON.stringify({ sources, fetchedAt: Date.now() }));
  } catch {
    // ignore storage errors
  }
}

export function useNodeSources(nodeId: string, nodeName: string, context?: { courseId?: string }) {
  const [sources, setSources] = useState<Source[]>([]);
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!nodeId) return;

    let cancelled = false;

    const load = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled || !session) return;

      const userId = session.user.id;
      const cached = loadSources(userId, nodeId);
      if (cached && cached.length > 0) {
        setSources(cached);
        return;
      }

      if (fetchedRef.current === nodeId) return;
      fetchedRef.current = nodeId;
      setLoading(true);

      try {
        const { data, error } = await supabase.functions.invoke("course-assistant", {
          body: {
            messages: [],
            mode: "sources",
            context: {
              nodeId,
              nodeName,
              courseId: context?.courseId ?? null,
              courseName: null,
              pageType: "learner",
            },
            _authToken: session.access_token,
          },
        });

        if (cancelled) return;

        if (!error && Array.isArray(data?.sources) && data.sources.length > 0) {
          setSources(data.sources);
          saveSources(userId, nodeId, data.sources);
        }
      } catch {
        // silent — further reading is non-critical
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    setSources([]);
    load();

    return () => { cancelled = true; };
  }, [nodeId]);

  return { sources, loading };
}
